export const OPTION_LISTS = {
  LEGAL_ENTITY: 'legalEntity',
  LOCATION: 'location',
  POSITION: 'position',
  AGENCY: 'agency',
} as const;

export type OptionListKey = (typeof OPTION_LISTS)[keyof typeof OPTION_LISTS];

export interface IListScope {
  // The form field holding the pick, and the name that pick travels under on the wire.
  field: 'legalEntityId' | 'locationId';
  param: string;
  // Set where the endpoint refuses the list without the pick: positions are bookable per unit and
  // answer Not Found, while locations are served whole and merely narrowed.
  waitsForScope?: boolean;
}

// The pick each list follows. `null` is written out rather than left absent so that a list added
// later is a decision and not a gap: a missing entry would load organisation-wide in silence.
export const LIST_SCOPES: Record<OptionListKey, IListScope | null> = {
  [OPTION_LISTS.LEGAL_ENTITY]: null,
  [OPTION_LISTS.LOCATION]: { field: 'legalEntityId', param: 'legal_entity_id' },
  [OPTION_LISTS.POSITION]: { field: 'locationId', param: 'company_id', waitsForScope: true },
  [OPTION_LISTS.AGENCY]: null,
};

export const DEEP_LINK_FIELDS = [
  { field: 'legalEntityId', list: OPTION_LISTS.LEGAL_ENTITY },
  { field: 'locationId', list: OPTION_LISTS.LOCATION },
  { field: 'positionId', list: OPTION_LISTS.POSITION },
  { field: 'agencyId', list: OPTION_LISTS.AGENCY },
] as const;

export const OPTION_LIST_PATHS: Record<OptionListKey, string> = {
  // Shared with the rest of dash rather than served under the simulator: the entities are the
  // organisation's own, and the run derives its entity from the unit, not from this pick.
  [OPTION_LISTS.LEGAL_ENTITY]: 'legal_entities/list',
  [OPTION_LISTS.LOCATION]: 'pricing/simulator/units/list',
  [OPTION_LISTS.POSITION]: 'pricing/simulator/positions/list',
  [OPTION_LISTS.AGENCY]: 'pricing/simulator/agencies/list',
};

// `loadFormData` appends `/form_data` to the path it is given; `createItem` posts to it as-is.
export const SIMULATOR_PATH = 'pricing/simulator';
export const RUNS_PATH = 'pricing/simulator/runs';

export const LIST_PAGE_SIZE = 20;
export const SEARCH_DEBOUNCE_MS = 300;

// Debounced so a burst of typing leaves one history entry rather than one per keystroke.
export const URL_SYNC_DEBOUNCE_MS = 400;

// A blocking finding replaces the result entirely; an advisory one renders beside it.
export const FINDING_SEVERITIES = {
  BLOCKING: 'blocking',
  ADVISORY: 'advisory',
} as const;

export type FindingSeverity = (typeof FINDING_SEVERITIES)[keyof typeof FINDING_SEVERITIES];

export const VALUE_KINDS = {
  MONEY_PER_HOUR: 'money_per_hour',
  MONEY_TOTAL: 'money_total',
  PERCENTAGE: 'percentage',
  HOURS: 'hours',
} as const;

export type ValueKind = (typeof VALUE_KINDS)[keyof typeof VALUE_KINDS];

export const PARITY = {
  PRE: 'pre_parity',
  POST: 'post_parity',
} as const;

export type Parity = (typeof PARITY)[keyof typeof PARITY];

// A period the run does not carry is read as zero worked rather than as unknown, so a rule written
// on a basis never asked about prices against nothing. `weekly_fte` is copied from weekly, not sent.
export const HOURS_FIELDS = [
  { name: 'hoursWorkedWeekly', period: 'weekly' },
  { name: 'hoursWorkedBiWeekly', period: 'bi_weekly' },
  { name: 'hoursWorkedDaily', period: 'daily' },
] as const;

export type HoursField = (typeof HOURS_FIELDS)[number]['name'];
export type HoursPeriod = (typeof HOURS_FIELDS)[number]['period'];

export const SHIFT_CONSTRAINTS = {
  age: { min: 17, max: 99 },
  tenureWeeks: { min: 0, max: 9999 },
  hoursWorked: { min: 0, max: 168, step: 0.25 },
  daysWorked: { min: 0, max: 6 },
  maxShiftMinutes: 24 * 60,
} as const;

// Held and sent as 24-hour `HH:mm`, whatever the country's display format.
export const TIME_VALUE_FORMAT = 'HH:mm';

export const MINUTES_PER_DAY = 24 * 60;

/** The only status whose body names the fields the run refused. */
export const RUN_UNPROCESSABLE = 422;

/** Why a run failed, so the card can say what to do about it instead of "try again" for every failure. */
export const RUN_ERROR_KINDS = {
  VALIDATION: 'validation',
  FORBIDDEN: 'forbidden',
  SERVER: 'server',
  NETWORK: 'network',
  /** A 2xx that carried nothing readable, or a result with no intervals to show. */
  MALFORMED: 'malformed',
  UNKNOWN: 'unknown',
} as const;

export type RunErrorKind = (typeof RUN_ERROR_KINDS)[keyof typeof RUN_ERROR_KINDS];
